// MonthlyHours — tenant-wide per-officer hours for a given calendar month.
// Phase 1H. Tab on /dashboard/schedules.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, RefreshCw, ServerCrash, Inbox } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import {
  fetchMonthlyHours, friendlyRosterError,
  type MonthlyHoursResponse, type MonthlyHoursRow,
} from "../lib/rosterApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";

function nowMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function monthShift(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}`;
}
function fmt(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export default function MonthlyHours(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;

  const [month, setMonth] = useState<string>(nowMonth());
  const [data, setData] = useState<MonthlyHoursResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetchMonthlyHours(month, tenantParam);
      setData(r);
    } catch (e) { setErr(friendlyRosterError(e)); }
    finally { setLoading(false); }
  }, [tenantParam, month]);
  useEffect(() => { void load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const header = ["officer_id", "officer_name", "shifts_count", "days_worked", "total_minutes", "total_hours"].join(",");
    const lines = data.items.map((r) =>
      [r.officer_id, csvEscape(r.officer_name ?? ""), r.shifts_count, r.days_worked, r.total_minutes, r.total_hours].join(","),
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `monthly-hours-${data.month}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        title="月時數"
        description="當月每位員工總工時 / 班次數 / 出勤天數，可匯出 CSV 對接薪資系統。"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setMonth((m) => monthShift(m, -1))} className="btn-secondary text-sm" aria-label="上個月"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setMonth(nowMonth())} className="btn-secondary text-sm">本月</button>
            <button onClick={() => setMonth((m) => monthShift(m, 1))} className="btn-secondary text-sm" aria-label="下個月"><ChevronRight className="h-4 w-4" /></button>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> 重新整理
            </button>
            <button className="btn-secondary text-sm" onClick={exportCsv} disabled={!data || data.items.length === 0}>
              <Download className="h-4 w-4" /> 匯出 CSV
            </button>
          </div>
        }
      />

      <div className="card mb-3 p-3 text-sm flex flex-wrap gap-4 items-center">
        <span className="text-text-secondary">月份：<span className="font-mono text-text-primary">{month}</span></span>
        {data && (
          <>
            <span className="text-text-secondary">員工數：<span className="text-text-primary">{data.totals.officers}</span></span>
            <span className="text-text-secondary">總班次：<span className="text-text-primary">{data.totals.total_shifts}</span></span>
            <span className="text-text-secondary">總時數：<span className="text-text-primary">{data.totals.total_hours} h</span></span>
          </>
        )}
      </div>

      {err && (
        <EmptyState icon={ServerCrash} title="月時數載入失敗" description={err}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />
      )}

      {!err && data && data.items.length === 0 && !loading && (
        <EmptyState icon={Inbox} title="這個月還沒有任何排班" description="去「月排班」tab 安排員工到班別後，這裡會出現統計。" />
      )}

      {!err && data && data.items.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="table-header">員工</th>
                <th className="table-header text-right w-24">班次</th>
                <th className="table-header text-right w-24">出勤天</th>
                <th className="table-header text-right w-28">時數 (h:mm)</th>
                <th className="table-header text-right w-28">總時數 (h)</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r: MonthlyHoursRow) => (
                <tr key={r.officer_id} className="table-row">
                  <td className="table-cell">
                    <div className="font-medium text-text-primary">{r.officer_name ?? r.officer_id}</div>
                    <div className="font-mono text-xs text-text-muted">{r.officer_id}</div>
                  </td>
                  <td className="table-cell text-right tabular-nums">{r.shifts_count}</td>
                  <td className="table-cell text-right tabular-nums">{r.days_worked}</td>
                  <td className="table-cell text-right tabular-nums font-mono">{fmt(r.total_minutes)}</td>
                  <td className="table-cell text-right tabular-nums font-mono text-text-primary">{r.total_hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function csvEscape(s: string): string {
  if (!/[",\n]/.test(s)) return s;
  return `"${s.replaceAll('"', '""')}"`;
}
